/**
 * DisputeCandidates — Wave 3 (Credit-Repair Family) ANCHOR migration.
 *
 * Route: /credit/disputes/:reportId
 *
 * This is the anchor route for Wave 3: the first true credit-repair
 * surface a user hits after `/credit-improvement`. Its aesthetic sets
 * the tone for the rest of the dispute-letter family (Wizard, Preview,
 * Detail, Letters list, Mailed list, Comparison).
 *
 * Rebuilt against the LandingV2 aesthetic bar (matched to Wave 2):
 *   • White surface cards (`--ds-color-surface`) on the warm cream
 *     page background (`--ds-color-background`). NO dark canvas.
 *   • 1px hairline `--ds-color-border` + `--ds-shadow-card` for
 *     elevation. Radius max `--ds-radius-xl` (14px).
 *   • Uppercase eyebrows via `text-[11px] tracking-[0.14em] font-semibold`.
 *   • Display headings via `--ds-font-display`, semibold,
 *     `letter-spacing: -0.02em`.
 *   • Composed from `@/design-system/*` primitives: `Modal` (focus-trap),
 *     `Select`, `Textarea`, `Button`, `Badge`, `SemanticStatusBadge`,
 *     `Alert`, `PageHeader`, `LoadingState`.
 *   • Byte-exact disclaimer surfaced in the generate-letter modal, on
 *     top of the entry disclaimer already rendered by
 *     `/credit-improvement` upstream.
 *
 * Material-scope preserved:
 *   • Every `data-testid` from the previous DisputeCandidates page is
 *     preserved verbatim.
 *   • All fetches, POST/DELETE endpoints, reason enum, address-gap
 *     profile-status gate, and the letter-mode / recipient selects
 *     unchanged.
 *   • Structured-data read-only contract enforced (only reason +
 *     custom_note are user-controllable).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  FileText,
  Loader2,
  ShieldAlert,
  UserCheck,
  UserX,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/api/client";
import AppNav from "@/components/app/AppNav";
import {
  Badge,
  Button,
  Modal,
  Select,
  SemanticStatusBadge,
  Textarea,
} from "@/design-system";

const REASON_OPTIONS = [
  { value: "not_mine", label: "This account is not mine" },
  { value: "incorrect_balance", label: "Incorrect balance" },
  { value: "incorrect_payment_history", label: "Incorrect payment history" },
  { value: "incorrect_account_status", label: "Incorrect account status" },
  { value: "incorrect_dates", label: "Incorrect dates" },
  { value: "duplicate_reporting", label: "Duplicate reporting" },
  { value: "paid_but_reporting_unpaid", label: "Paid but reporting unpaid" },
  { value: "closed_but_reporting_open", label: "Closed but reporting open" },
  { value: "unauthorized_inquiry", label: "Unauthorized inquiry" },
  { value: "previously_disputed_not_corrected", label: "Previously disputed but not corrected" },
  { value: "other_factual_error", label: "Other factual error" },
];

const RECIPIENTS = [
  { value: "experian",   label: "Experian (bureau)" },
  { value: "equifax",    label: "Equifax (bureau)" },
  { value: "transunion", label: "TransUnion (bureau)" },
];

const MODES = [
  { value: "bureau_dispute", label: "Bureau dispute (default)" },
  { value: "furnisher_dispute", label: "Furnisher (creditor) dispute" },
  { value: "inquiry_dispute", label: "Unauthorized inquiry dispute" },
  { value: "identity_theft_dispute", label: "Identity theft dispute" },
  { value: "mov_followup", label: "Method-of-Verification follow-up" },
  { value: "results_followup", label: "Results follow-up" },
];

const SEVERITY_TONE = {
  high:     "danger",
  critical: "danger",
  medium:   "warning",
  low:      "info",
};

/* ------------------------------- root ---------------------------------- */

export default function DisputeCandidates() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selections, setSelections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [flagOff, setFlagOff] = useState(false);
  const [selecting, setSelecting] = useState({}); // finding_id → { reason, custom_note }
  const [generateOpen, setGenerateOpen] = useState(false);
  const [genMode, setGenMode] = useState("bureau_dispute");
  const [genRecipient, setGenRecipient] = useState("experian");
  const [profileStatus, setProfileStatus] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const generateTriggerRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setFlagOff(false);
    try {
      const [candidatesRes, selectionsRes, profileRes] = await Promise.all([
        api.get(`/credit-engine/disputes/report/${reportId}/candidates`),
        api.get(`/credit-engine/disputes/selections?report_id=${reportId}`),
        api.get(`/credit-engine/disputes/profile-status`),
      ]);
      setItems(candidatesRes.data?.items || []);
      setSelections(selectionsRes.data?.items || []);
      setProfileStatus(profileRes.data || null);
    } catch (e) {
      if (e?.response?.status === 503) {
        setFlagOff(true);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/site-settings")
      .then((res) => { if (!cancelled) setSiteSettings(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const selectionByFinding = useMemo(() => {
    const map = {};
    for (const s of selections) {
      if (s.status !== "withdrawn") map[s.finding_id] = s;
    }
    return map;
  }, [selections]);

  const activeSelectionsCount = useMemo(
    () => selections.filter((s) => s.status !== "withdrawn").length,
    [selections],
  );

  async function createSelection(finding_id) {
    const draft = selecting[finding_id] || {};
    if (!draft.reason) {
      toast.error("Pick a dispute reason first");
      return;
    }
    if (draft.reason === "other_factual_error" && !(draft.custom_note || "").trim()) {
      toast.error("'Other factual error' requires a custom note");
      return;
    }
    setCreating(true);
    try {
      const finding = items.find((it) => it.finding_id === finding_id);
      await api.post(`/credit-engine/disputes/report/${reportId}/select`, {
        finding_id,
        tradeline_ids: (finding?.tradelines || []).map((t) => t.tradeline_id),
        dispute_reason: draft.reason,
        custom_note: (draft.custom_note || "").slice(0, 500),
      });
      toast.success("Selection saved");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  }

  async function withdrawSelection(selection_id) {
    try {
      await api.delete(`/credit-engine/disputes/selections/${selection_id}`);
      toast.success("Selection withdrawn");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function generateLetter() {
    const active = selections.filter(
      (s) => s.status !== "withdrawn" && s.status !== "letter_finalized",
    );
    if (!active.length) {
      toast.error("Add at least one dispute selection first");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post(`/credit-engine/letters/`, {
        selection_ids: active.map((s) => s.id),
        mode: genMode,
        recipient_key: genRecipient,
      });
      toast.success(`Letter draft #${res.data?.id?.slice(-6) || ""} generated`);
      navigate(`/credit/letters/${res.data?.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
      setGenerateOpen(false);
    }
  }

  if (flagOff) {
    return (
      <Shell>
        <div
          data-testid="disputes-flag-off"
          className="rounded-[var(--ds-radius-lg)] p-10 md:p-14 text-center"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
        >
          <span
            className="inline-flex h-12 w-12 items-center justify-center rounded-[var(--ds-radius-md)]"
            style={{ background: "var(--ds-color-warning-soft)", color: "var(--ds-color-warning)" }}
          >
            <ShieldAlert className="w-6 h-6" aria-hidden />
          </span>
          <h1
            className="mt-5 text-[var(--ds-color-ink)]"
            style={{
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontSize: "clamp(1.5rem, 2.4vw, 1.875rem)",
            }}
          >
            Dispute drafting is temporarily disabled.
          </h1>
          <p className="mt-3 max-w-lg mx-auto text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)]">
            The letter generator is currently disabled behind the{" "}
            <code className="text-[13px] font-mono">letter_generator_v2</code> flag. Contact
            support to enable it for your account.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {/* Formal page header */}
      <header className="pt-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="disputes-back-btn"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors mb-6"
          style={{ transitionDuration: "var(--ds-duration-fast)" }}
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden />
          Back
        </button>

        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
          Credit Repair · Dispute Candidates
        </div>
        <div className="mt-2 flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 max-w-2xl">
            <h1
              className="text-[var(--ds-color-ink)] leading-tight"
              style={{
                fontFamily: "var(--ds-font-display)",
                fontWeight: 600,
                letterSpacing: "-0.02em",
                fontSize: "clamp(2rem, 3.4vw, 2.75rem)",
              }}
            >
              Select the items you want to dispute.
            </h1>
            <p className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)]">
              Every finding on this credit report is listed below. Pick a
              dispute reason for each item you want to challenge, then
              generate a compliant letter draft. Educational analysis — you
              retain full control before mailing.
            </p>
          </div>
          {profileStatus && (
            <ProfileStatusChip
              status={profileStatus}
              onFix={() => navigate("/profile")}
            />
          )}
        </div>
      </header>

      {/* Profile remediation banner (address gap gate) */}
      {profileStatus && !profileStatus.complete && (
        <section
          data-testid="disputes-profile-remediation-banner"
          className="rounded-[var(--ds-radius-lg)] p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{
            background: "var(--ds-color-warning-soft)",
            border: "1px solid var(--ds-color-warning)",
          }}
        >
          <div className="flex items-start gap-3 min-w-0">
            <UserX
              className="w-4 h-4 mt-0.5 flex-shrink-0"
              style={{ color: "var(--ds-color-warning)" }}
              aria-hidden
            />
            <div className="min-w-0">
              <div
                className="text-[var(--ds-color-ink)]"
                style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
              >
                Your profile is missing sender-identity fields.
              </div>
              <p className="mt-1 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
                {profileStatus.message}
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("/profile")}
            data-testid="disputes-profile-remediation-cta"
          >
            Complete profile
            <ChevronRight className="w-3.5 h-3.5" aria-hidden />
          </Button>
        </section>
      )}

      {/* Selection summary + generate-letter CTA */}
      {activeSelectionsCount > 0 && (
        <section
          data-testid="disputes-selection-summary"
          className="rounded-[var(--ds-radius-lg)] p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
        >
          <div className="flex items-start gap-3 min-w-0">
            <span
              className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[var(--ds-radius-md)]"
              style={{ background: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)" }}
            >
              <FileText className="w-4 h-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-accent)]">
                Ready to send
              </div>
              <div
                className="mt-1 text-[var(--ds-color-ink)]"
                style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.125rem" }}
              >
                <span className="tabular-nums">{activeSelectionsCount}</span> selection
                {activeSelectionsCount === 1 ? "" : "s"} queued.
              </div>
              <p className="mt-1 text-[13px] text-[var(--ds-color-ink-muted)]">
                Generate a letter draft when you&rsquo;re finished picking items.
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            size="md"
            disabled={creating}
            loading={creating}
            data-testid="disputes-generate-letter-btn"
            ref={generateTriggerRef}
            onClick={() => setGenerateOpen(true)}
          >
            <FileText className="w-4 h-4" aria-hidden />
            Generate letter draft
          </Button>
        </section>
      )}

      {/* Candidates list */}
      {loading ? (
        <div className="flex items-center gap-2 text-[13px] text-[var(--ds-color-ink-muted)]">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
          Loading candidates…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="disputes-empty"
          className="rounded-[var(--ds-radius-lg)] p-10 md:p-14 text-center"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
        >
          <h2
            className="text-[var(--ds-color-ink)]"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.5rem", letterSpacing: "-0.02em" }}
          >
            No dispute candidates on this report.
          </h2>
          <p className="mt-2 text-[13px] text-[var(--ds-color-ink-muted)]">
            Nothing here needs a dispute right now — a clean signal.
          </p>
        </div>
      ) : (
        <section className="space-y-4">
          <SectionEyebrow>Findings ({items.length})</SectionEyebrow>
          <ol className="space-y-4">
            {items.map((it) => (
              <CandidateCard
                key={it.finding_id}
                finding={it}
                selection={selectionByFinding[it.finding_id]}
                draft={selecting[it.finding_id] || { reason: "", custom_note: "" }}
                onDraftChange={(partial) =>
                  setSelecting((s) => ({
                    ...s,
                    [it.finding_id]: {
                      ...(s[it.finding_id] || { reason: "", custom_note: "" }),
                      ...partial,
                    },
                  }))
                }
                onAdd={() => createSelection(it.finding_id)}
                onWithdraw={() => withdrawSelection(selectionByFinding[it.finding_id].id)}
                creating={creating}
              />
            ))}
          </ol>
        </section>
      )}

      {/* Generate letter modal */}
      <Modal
        open={generateOpen}
        onOpenChange={setGenerateOpen}
        title="Generate letter draft"
        description="The letter body is deterministic; only the opening paragraph and the detailed factual dispute are AI-enriched inside strict validators."
        data-testid="disputes-generate-modal"
        returnFocusRef={generateTriggerRef}
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              data-testid="disputes-generate-cancel-btn"
              onClick={() => setGenerateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              data-testid="disputes-generate-confirm-btn"
              onClick={generateLetter}
              disabled={creating}
              loading={creating}
            >
              <FileText className="w-3.5 h-3.5" aria-hidden />
              Generate draft
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="disputes-mode"
              className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-2"
            >
              Letter mode
            </label>
            <Select
              id="disputes-mode"
              value={genMode}
              onChange={(e) => setGenMode(e.target.value)}
              options={MODES}
              data-testid="disputes-generate-mode-select"
            />
          </div>
          <div>
            <label
              htmlFor="disputes-recipient"
              className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-2"
            >
              Recipient
            </label>
            <Select
              id="disputes-recipient"
              value={genRecipient}
              onChange={(e) => setGenRecipient(e.target.value)}
              options={RECIPIENTS}
              data-testid="disputes-generate-recipient-select"
            />
          </div>
          {siteSettings?.disclaimer_text && (
            <div
              data-testid="disputes-generate-disclaimer"
              className="rounded-[var(--ds-radius-md)] p-3 text-[12px] leading-relaxed"
              style={{
                background: "var(--ds-color-surface-muted)",
                border: "1px solid var(--ds-color-border)",
                color: "var(--ds-color-ink-muted)",
              }}
            >
              {siteSettings.disclaimer_text}
            </div>
          )}
        </div>
      </Modal>
    </Shell>
  );
}

/* --------------------------- Candidate card ---------------------------- */

function CandidateCard({
  finding, selection, draft, onDraftChange, onAdd, onWithdraw, creating,
}) {
  const severityTone = SEVERITY_TONE[finding.severity] || "warning";
  const bureaus = (finding.tradelines || []).map((t) => t.bureau).filter(Boolean);
  const uniqueBureaus = [...new Set(bureaus)];
  const primaryCreditor = finding.tradelines?.[0]?.creditor_name || "Account";
  return (
    <li
      data-testid={`disputes-candidate-${finding.finding_id}`}
      className="rounded-[var(--ds-radius-lg)] p-5 md:p-6"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
              Rule {finding.rule_id}
            </span>
            <span className="text-[11px] text-[var(--ds-color-ink-subtle)]">·</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
              {finding.category}
            </span>
            <Badge tone={severityTone}>
              {finding.severity}
            </Badge>
          </div>
          <h3
            className="mt-2 text-[var(--ds-color-ink)]"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.35 }}
          >
            {primaryCreditor}
          </h3>
          {uniqueBureaus.length > 0 && (
            <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[12px] text-[var(--ds-color-ink-muted)]">
              <span>Reported by:</span>
              {uniqueBureaus.map((b) => (
                <Badge key={b} tone="neutral" size="sm">{b}</Badge>
              ))}
            </div>
          )}
        </div>
        {selection && (
          <Badge
            tone="success"
            data-testid={`disputes-badge-selected-${finding.finding_id}`}
          >
            <Check className="w-3 h-3" aria-hidden />
            {selection.dispute_reason.replace(/_/g, " ")}
          </Badge>
        )}
      </header>

      {finding.analyst_summary?.account_matters && (
        <div
          className="mt-4 rounded-[var(--ds-radius-md)] p-3 text-[13px] leading-relaxed"
          data-testid={`disputes-analyst-summary-${finding.finding_id}`}
          style={{ background: "var(--ds-color-surface-muted)", border: "1px solid var(--ds-color-border)" }}
        >
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-1">
            Why this matters
          </div>
          <div className="text-[var(--ds-color-ink)]">
            {finding.analyst_summary.account_matters}
          </div>
        </div>
      )}

      <ul className="mt-4 grid gap-2 md:grid-cols-2 text-[13px] leading-relaxed">
        {(finding.tradelines || []).map((t) => (
          <li
            key={t.tradeline_id}
            className="rounded-[var(--ds-radius-md)] p-3"
            style={{ background: "var(--ds-color-surface-muted)", border: "1px solid var(--ds-color-border)" }}
          >
            <div className="text-[var(--ds-color-ink)]">
              {t.creditor_name}{" "}
              <span className="text-[var(--ds-color-ink-muted)]">·</span>{" "}
              <span className="text-[var(--ds-color-ink-muted)]">{t.bureau}</span>
            </div>
            <div className="mt-1 text-[12px] text-[var(--ds-color-ink-muted)]">
              {t.account_type} · {t.account_status}
              {t.account_number_last4 ? ` · ending ${t.account_number_last4}` : ""}
            </div>
          </li>
        ))}
      </ul>

      {selection ? (
        <div className="mt-5">
          <Button
            variant="secondary"
            size="sm"
            onClick={onWithdraw}
            data-testid={`disputes-withdraw-btn-${finding.finding_id}`}
          >
            Withdraw selection
          </Button>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          <div>
            <label
              htmlFor={`disputes-reason-${finding.finding_id}`}
              className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-2"
            >
              Dispute reason
            </label>
            <Select
              id={`disputes-reason-${finding.finding_id}`}
              value={draft.reason}
              onChange={(e) => onDraftChange({ reason: e.target.value })}
              placeholder="— pick a reason —"
              options={REASON_OPTIONS}
              className="md:max-w-md"
              data-testid={`disputes-reason-select-${finding.finding_id}`}
            />
          </div>
          <Textarea
            rows={3}
            maxLength={500}
            placeholder="Optional context (max 500 chars) — never used as fact source, only paraphrased into your contention."
            value={draft.custom_note}
            onChange={(e) => onDraftChange({ custom_note: e.target.value })}
            data-testid={`disputes-custom-note-${finding.finding_id}`}
          />
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={onAdd}
              disabled={creating}
              loading={creating}
              data-testid={`disputes-add-btn-${finding.finding_id}`}
            >
              <ChevronRight className="w-3.5 h-3.5" aria-hidden />
              Add to selections
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

/* --------------------------- Sub-primitives ---------------------------- */

function ProfileStatusChip({ status, onFix }) {
  const complete = !!status.complete;
  return (
    <button
      type="button"
      onClick={complete ? undefined : onFix}
      className="inline-flex items-center gap-2 flex-shrink-0"
      style={{ cursor: complete ? "default" : "pointer" }}
      data-testid={complete ? "disputes-profile-complete-badge" : "disputes-profile-incomplete-badge"}
      title={status.message || undefined}
    >
      <Badge tone={complete ? "success" : "warning"} size="md">
        {complete ? (
          <UserCheck className="w-3.5 h-3.5" aria-hidden />
        ) : (
          <UserX className="w-3.5 h-3.5" aria-hidden />
        )}
        {complete ? "Profile complete" : "Address needed — complete profile"}
      </Badge>
      {!complete && <ChevronRight className="w-3.5 h-3.5 text-[var(--ds-color-ink-muted)]" aria-hidden />}
    </button>
  );
}

function Shell({ children }) {
  return (
    <div
      data-testid="disputes-root"
      data-page-outer="wave-3"
      className="min-h-screen bg-[var(--ds-color-background)] text-[var(--ds-color-ink)]"
    >
      <AppNav />
      <main className="max-w-5xl mx-auto px-4 md:px-8 pt-10 pb-20 space-y-8">
        {children}
      </main>
    </div>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
      {children}
    </div>
  );
}
